// DispatchDeck — Person compensation accessor (TASK-1481)
// ============================================================================
// `window.PersonComp` — the ONE way client code resolves a person's
// compensation fields now that they are moving off the broadcast `people`
// collection into the admin/HR-gated sibling `tenants/{tid}/peopleComp/{personId}`
// (the peopleTax precedent: `people` is synced to EVERY session including
// driver-PIN browsers, so any field more sensitive than name/phone/role
// belongs in a parallel collection with a tighter rule).
//
// WHY THIS EXISTS. firestore.rules:~1230 `allow read: if canReadTenant(...)`
// on `people/{personId}` + `canReadTenant` including `hasDriverSession` meant
// any driver who completed a normal PIN login could raw-SDK read every
// employee's hrHourlyRate / hrAnnualSalary / hrBCBS / hrDeductions — live on
// every tenant (TASK-1481, same family as the April 2026 SSN leak that
// created peopleTax). Firestore cannot field-redact a read, so the ONLY fix
// is moving the fields. But TITS runs REAL payroll off hrHourlyRate, and a
// missed read site pays $0 SILENTLY — not an error. Hence this accessor:
// every consumer resolves through `PersonComp.effective(person)`, which
// overlays the peopleComp doc when one exists and FALLS BACK to the legacy
// person-doc fields when it doesn't, so behavior is byte-identical before the
// migration/strip and correct after it.
//
// LOADING (payRates.js / equipmentNotes.js precedent — CLAUDE.md Pitfall #1):
// imported ONCE via a side-effect import from views/clock.js (which is in
// app.js's static import graph, so this evaluates at startup before any view
// handler runs). EVERY other module reads `window.PersonComp` at call time —
// never a second `?v=` import (two busters = two module instances = ghost
// state). firebase-config.js is imported with the SAME pinned `?v=1867` every
// other importer uses, so it stays one instance.
//
// THE COMP FIELD SET (the 10 fields that move — keep in lockstep with
// scripts/migrate-comp-to-peoplecomp.cjs, scripts/verify-comp-parity.cjs and
// the firestore.rules peopleComp block / people-update denylist):
//   hrHourlyRate, hrAnnualSalary, hrPerDiem, hrBCBS, hrDeductions,
//   flsaStatus, hrOvertimeExempt, payType, payRate, payRateUnit
// NOTE hrEmploymentType ('salary'|'hourly') deliberately STAYS on the person
// doc — it reveals a pay *basis*, not an amount, and the salaried-exempt OT
// gate (clock.js isPayrollExempt) needs it in driver-readable scope so a
// driver's own My-Timesheet OT display can't diverge from payroll.
//
// 🔴 DENIED ≠ ABSENT. A member without admin/hr-directory gets
// permission-denied on the peopleComp subscription. That is NOT "no comp
// data" — it is "you cannot know". The accessor records that state
// (`isDenied()`), and money-computing surfaces must check `compUnknown(person)`
// / `exportGuard(people)` and visibly refuse ("no access to pay rates")
// instead of silently rendering $0. Pre-strip the person doc still carries
// the legacy fields, so denied members keep working off the fallback —
// `compUnknown` only trips once the fields are gone from the person doc too
// (the post-strip signature), which is exactly when $0 would otherwise ship.
//
// MERGE SEMANTICS: a comp field KEY PRESENT on the peopleComp doc (including
// an explicit null) wins over the person doc; an ABSENT key falls through.
// This makes a partial comp doc safe — e.g. the comp-review raise path writes
// only hrHourlyRate until the migration backfills the rest.
//
// The pure core lives in `_personCompCoreFactory` (no Firebase, no DOM) so
// tests/person-comp.unit.cjs can extract and run it in node:vm — the same
// harness pattern as tests/mute-slate.unit.cjs.

import {
  db,
  collection,
  doc,
  setDoc,
  onSnapshot,
  Timestamp,
} from './firebase-config.js?v=1868';

// ── pure core (vm-testable, no Firebase) ────────────────────────────────────
function _personCompCoreFactory() {
  'use strict';

  const COMP_FIELDS = [
    'hrHourlyRate', 'hrAnnualSalary', 'hrPerDiem', 'hrBCBS', 'hrDeductions',
    'flsaStatus', 'hrOvertimeExempt', 'payType', 'payRate', 'payRateUnit',
  ];

  // status: 'idle' (never subscribed) | 'loading' (subscribed, no snapshot yet)
  //       | 'ready' (snapshot live) | 'denied' (permission-denied — the caller
  //         CANNOT know comp data; distinct from an empty collection, which is
  //         'ready' with an empty map) | 'error' (transient non-permission
  //         failure — treated like loading: fall back, don't refuse).
  const state = { tenantId: null, status: 'idle', map: new Map() };

  // Subset of the 10 comp fields present on `src` (undefined keys dropped —
  // Firestore throws on undefined, and an undefined must never clobber a
  // stored value through a merge write).
  function pickComp(src) {
    const out = {};
    if (!src) return out;
    COMP_FIELDS.forEach((k) => {
      if (src[k] !== undefined) out[k] = src[k];
    });
    return out;
  }

  function get(personId) {
    return (personId && state.map.get(personId)) || null;
  }

  // The one resolver every read site uses. Comp doc present → its keys win
  // (per-key, so partial docs are safe); absent → the person doc's own legacy
  // fields survive untouched (pre-migration behavior, byte-identical).
  function effective(person) {
    if (!person) return person;
    const comp = person.id ? state.map.get(person.id) : null;
    if (!comp) return person;
    const merged = Object.assign({}, person);
    COMP_FIELDS.forEach((k) => {
      if (k in comp) merged[k] = comp[k];
    });
    return merged;
  }

  // Hourly rate convenience (comp-first, then legacy fallback via effective).
  function rate(person) {
    const e = effective(person);
    const n = parseFloat(e && e.hrHourlyRate);
    return Number.isFinite(n) && n > 0 ? n : 0;
  }

  function status() { return state.status; }
  function isDenied() { return state.status === 'denied'; }

  // Does this person doc itself carry ANY of the comp fields? (The legacy
  // fallback signature. After the strip step this returns false for everyone.)
  function personHasOwnComp(person) {
    if (!person) return false;
    return COMP_FIELDS.some((k) => {
      const v = person[k];
      return v !== undefined && v !== null && v !== '' && v !== 0 && v !== false;
    });
  }

  // "Cannot know" covers BOTH denial and a never-loaded transient failure
  // (COD Domain 2026-08-13): a Firestore hiccup on FIRST load right after the
  // strip deploy would otherwise fall through to a person doc that no longer
  // carries the fields — the exact silent-$0 this module exists to prevent.
  // Once a snapshot HAS landed, a later error keeps serving the last-known
  // map (stale-but-real beats refusing a working payroll screen).
  function cannotKnow() { return state.status === 'denied' || state.status === 'error'; }

  // 🔴 DENIED ≠ ABSENT — the per-person refusal predicate. True only when we
  // CANNOT KNOW the comp collection (denied, or never-loaded error) AND the
  // person doc carries no legacy comp fields to fall back on: the caller
  // cannot distinguish "no rate set" from "rate hidden from you", so surfaces
  // must say "no access to pay rates", never render $0. Pre-strip this never
  // trips (legacy fields present); post-strip it trips for exactly the
  // members the rules exclude (plus a first-load outage, which must also
  // refuse rather than zero).
  function compUnknown(person) {
    if (!cannotKnow()) return false;
    return !personHasOwnComp(person);
  }

  // Export-level guard for payroll/BI bulk money surfaces. Blocks when we
  // CANNOT KNOW the comp collection (denied, or never-loaded error) AND none
  // of the people carry legacy comp fields (the post-strip signature) — i.e.
  // every dollar the export would print is unknowable, not merely zero. A
  // tenant with genuinely no pay data configured AND an unknowing reader also
  // blocks here; that is accepted — an all-$0 payroll export for a reader who
  // cannot verify it is not a loss.
  function exportGuard(people) {
    const list = Array.isArray(people) ? people.filter(Boolean) : [];
    if (!cannotKnow() || !list.length) return { blocked: false, reason: null };
    const anyLegacy = list.some(personHasOwnComp);
    if (anyLegacy) return { blocked: false, reason: null };
    return {
      blocked: true,
      reason: isDenied()
        ? 'You do not have access to pay rates (HR Directory access is required), so this export would show $0 for everyone. Ask an admin to run it or grant access.'
        : 'Pay rates could not be loaded (connection problem), so this export would show $0 for everyone. Check your connection and try again.',
    };
  }

  // Snapshot plumbing (called by the Firebase wiring below; also directly by
  // unit tests to simulate snapshots/errors).
  function _applySnapshot(docs) {
    state.map = new Map();
    (docs || []).forEach((d) => { if (d && d.id) state.map.set(d.id, d.data || d); });
    state.status = 'ready';
  }
  function _applyError(code) {
    // A snapshot that already landed keeps serving (stale-but-real beats
    // refusing a working payroll screen mid-session); only a never-loaded
    // listener degrades to a refusal state. Denial always wins — it means the
    // rules said no, regardless of any earlier cached snapshot.
    if (code === 'permission-denied') { state.status = 'denied'; return; }
    if (state.status !== 'ready') state.status = 'error';
  }
  function _reset(tenantId) {
    state.tenantId = tenantId || null;
    state.status = tenantId ? 'loading' : 'idle';
    state.map = new Map();
  }

  return {
    COMP_FIELDS, pickComp, get, effective, rate, status, isDenied, cannotKnow,
    personHasOwnComp, compUnknown, exportGuard,
    _applySnapshot, _applyError, _reset, _state: state,
  };
}

// ── Firebase wiring ─────────────────────────────────────────────────────────
const _core = _personCompCoreFactory();
let _unsubscribe = null;
// Re-render hooks keyed by view name ('hr', 'bi', 'clock', 'billing') so a
// view re-registering on re-init replaces its old callback instead of leaking
// a pile of stale closures.
const _listeners = new Map();

function _notify() {
  _listeners.forEach((cb) => {
    try { cb(); } catch (e) { console.warn('[PersonComp] listener failed:', e && e.message); }
  });
}

// Idempotent per tenant: each comp-consuming view calls this from its own
// data-subscription path (clock/hr/bi/billing all subscribe to `people`
// already; this mirrors that). Same tenant → just (re)registers the
// listener. New tenant → tears down and resubscribes.
//
// The error arm is the DENIED lane: a clock-only member or a driver-PIN
// session subscribing here is EXPECTED to be denied once the rules land —
// that is the leak being fixed, not a bug. We record the state (so money
// surfaces can refuse) and stay quiet in the console beyond one warn.
function subscribe(tenantId, opts) {
  const key = (opts && opts.key) || 'default';
  if (opts && typeof opts.onUpdate === 'function') _listeners.set(key, opts.onUpdate);
  if (!tenantId) return;
  if (_core._state.tenantId === tenantId && _unsubscribe) return;
  if (_unsubscribe) { try { _unsubscribe(); } catch (e) { /* already dead */ } _unsubscribe = null; }
  _core._reset(tenantId);
  _unsubscribe = onSnapshot(
    collection(db, 'tenants', tenantId, 'peopleComp'),
    (snap) => {
      _core._applySnapshot(snap.docs.map((d) => ({ id: d.id, data: d.data() })));
      _notify();
    },
    (err) => {
      const code = err && err.code;
      _core._applyError(code);
      if (code !== 'permission-denied') {
        console.warn('[PersonComp] peopleComp subscription error:', (err && err.message) || err);
      }
      _notify();
    }
  );
}

// Dual-write helper — writers (saveEmploymentFields, the comp-review raise,
// saveTaxFields) call this AFTER their person-doc write so the comp store
// stays the source of truth. Merge semantics; only the 10 comp fields are
// ever written (pickComp drops everything else INCLUDING undefined, which
// Firestore would throw on).
//
// Returns { ok, code }:
//   ok:true                → landed (or nothing comp-relevant to write)
//   ok:false code:'permission-denied' → the caller's access level can't write
//        the comp store. Two ways this happens: (a) this client shipped
//        BEFORE the firestore.rules peopleComp block — every save hits it
//        (the integration MUST deploy rules first, blast-radius sequencing);
//        (b) post-rules, an editor without admin/the comp write grant. Either
//        way the caller shows a WARNING with actionable copy, not a scary
//        error — but it must never be silent, because if a comp doc already
//        exists it is now STALE and it WINS over the person doc for readers
//        who can see it.
//   ok:false code:<other>  → transient failure; the caller shows a retry
//        ERROR (the comp doc is stale and wins — same drift risk).
async function writeComp(tenantId, personId, fields) {
  if (!tenantId || !personId) return { ok: false, code: 'bad-args' };
  const payload = _core.pickComp(fields);
  if (!Object.keys(payload).length) return { ok: true, code: null }; // nothing comp-relevant changed
  try {
    payload.personId = personId;
    payload.updatedAt = Timestamp.now();
    await setDoc(doc(db, 'tenants', tenantId, 'peopleComp', personId), payload, { merge: true });
    return { ok: true, code: null };
  } catch (err) {
    console.warn('[PersonComp] peopleComp dual-write failed for', personId, (err && err.message) || err);
    return { ok: false, code: (err && err.code) || 'unknown' };
  }
}

window.PersonComp = {
  COMP_FIELDS: _core.COMP_FIELDS,
  pickComp: _core.pickComp,
  get: _core.get,
  effective: _core.effective,
  rate: _core.rate,
  status: _core.status,
  isDenied: _core.isDenied,
  cannotKnow: _core.cannotKnow,
  personHasOwnComp: _core.personHasOwnComp,
  compUnknown: _core.compUnknown,
  exportGuard: _core.exportGuard,
  subscribe,
  writeComp,
};
